import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { courseApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, Spinner, StatCard } from '../../components/shared/index';
import clsx from 'clsx';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  completed:    'bg-green-100  text-green-800',
  in_progress:  'bg-amber-100  text-amber-800',
  assigned:     'bg-gray-100   text-gray-700',
  not_enrolled: 'bg-red-100    text-red-800',
  failed:       'bg-red-100    text-red-800',
};

function StatusCell({ status, overdue }) {
  return (
    <div className="flex items-center gap-1">
      <span className={clsx('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium', STATUS_STYLES[status] || STATUS_STYLES.not_enrolled)}>
        {status === 'not_enrolled' ? 'not enrolled' : status.replace('_',' ')}
      </span>
      {overdue && <ExclamationTriangleIcon className="w-3.5 h-3.5 text-red-500" title="Overdue" />}
    </div>
  );
}

export default function MandatoryCompliance() {
  const { user } = useAuth();
  const isHr     = ['hr_admin','super_admin'].includes(user?.role);
  const [search, setSearch] = useState('');
  const [onlyBehind, setOnlyBehind] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['mandatory-status'],
    queryFn:  () => courseApi.mandatoryStatus().then(r => r.data),
    enabled:  isHr,
  });

  const rows    = data?.rows    || [];
  const courses = data?.courses || [];

  const filtered = useMemo(() => rows.filter(r => {
    if (search && !r.employee_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (onlyBehind && r.compliance_pct >= 100) return false;
    return true;
  }), [rows, search, onlyBehind]);

  const stats = useMemo(() => {
    if (rows.length === 0) return { total: 0, compliant: 0, overdue: 0, avg: 0 };
    const compliant = rows.filter(r => r.compliance_pct >= 100).length;
    const overdue   = rows.reduce((s, r) => s + r.overdue_count, 0);
    const avg       = Math.round(rows.reduce((s, r) => s + r.compliance_pct, 0) / rows.length);
    return { total: rows.length, compliant, overdue, avg };
  }, [rows]);

  if (!isHr) {
    return (
      <div className="card">
        <EmptyState title="HR view" description="Mandatory compliance reporting is available to HR admins and super admins." />
      </div>
    );
  }

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  if (courses.length === 0) {
    return (
      <div className="card">
        <EmptyState
          title="No mandatory courses defined"
          description="Mark courses as mandatory in the Course Catalog — optionally scoped to specific roles — to track compliance here."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard title="Employees tracked" value={stats.total}     color="blue"   icon={UserGroupIcon} />
        <StatCard title="Fully compliant"   value={stats.compliant} color="green"  icon={CheckBadgeIcon} />
        <StatCard title="Overdue items"     value={stats.overdue}   color="red"    icon={ExclamationTriangleIcon} />
        <StatCard title="Avg compliance"    value={`${stats.avg}%`} color="purple" icon={ShieldCheckIcon} />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyBehind} onChange={e => setOnlyBehind(e.target.checked)} />
          Show only behind
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No employees match"
            description={onlyBehind ? 'Everyone matching your filters is fully compliant — great work!' : 'Try clearing filters.'}
          />
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="th sticky left-0 bg-gray-50 z-10">Employee</th>
                <th className="th">Role</th>
                <th className="th">Compliance</th>
                {courses.map(c => (
                  <th key={c.id} className="th whitespace-nowrap max-w-[180px] truncate" title={c.title}>{c.title}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map(r => (
                <tr key={r.employee_id} className="tr-hover">
                  <td className="td sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                        {r.employee_name[0]}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{r.employee_name}</span>
                    </div>
                  </td>
                  <td className="td text-xs capitalize">{r.role?.replace('_',' ')}</td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      <span className={clsx('text-sm font-semibold', {
                        'text-green-600':  r.compliance_pct >= 100,
                        'text-amber-600':  r.compliance_pct >= 50 && r.compliance_pct < 100,
                        'text-red-600':    r.compliance_pct < 50,
                      })}>{r.compliance_pct}%</span>
                      <span className="text-xs text-gray-400">{r.completed_count}/{r.required_count}</span>
                    </div>
                  </td>
                  {courses.map(c => {
                    const item = r.items.find(i => i.course_id === c.id);
                    if (!item) return <td key={c.id} className="td text-center text-gray-300 text-xs">n/a</td>;
                    return (
                      <td key={c.id} className="td">
                        <StatusCell status={item.status} overdue={item.is_overdue} />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
